import type { CourseVerification, VerificationCourse } from "../types/courseVerification";
import { normalizeRaceRules } from "./raceRules";
import { normalizeMediaUrl } from "./mediaUrls";
import { parsePlateNumber } from "./racePlate";
import { normalizeVenueAmenities } from "../types/venueAmenities";
import {
  isSignupAuthMethod,
  isSignupPlatform,
} from "../constants/accountAcquisition";
import {
  AidStation,
  Checkpoint,
  EMERGENCY_RELATIONSHIPS,
  EmergencyContact,
  EVENT_ENTRY_TYPES,
  EVENT_STATUSES,
  EVENT_TYPES,
  Event,
  EventEntryType,
  EventStatus,
  EventType,
  FeatureFlags,
  GearItem,
  KompeteLocation,
  KompeteUser,
  LegalDisclaimer,
  RacePaymentSettings,
  RaceProximitySettings,
  RACE_PROXIMITY_DEFAULTS,
  Race,
  RACE_CATEGORIES,
  RaceCategory,
  RaceCue,
  RACE_DIFFICULTIES,
  RaceDifficulty,
  RACE_ENDURANCE_FORMATS,
  RaceEnduranceFormat,
  RaceFormat,
  RACE_FORMAT_KINDS,
  RaceFormatKind,
  RaceResult,
  REFUND_POLICY_TYPES,
  RegistrationPricingSnapshot,
  RegistrationTaxQuote,
  RegistrationTaxPolicy,
  RegistrationRecord,
  REGISTRATION_STATUSES,
  RACE_START_POLICIES,
  RaceStartPolicy,
  RacerLocation,
  REFUND_STATUSES,
  RefundPolicyType,
  RACE_SURFACE_TYPES,
  RaceSurfaceType,
  SPORT_TYPES,
  SportType,
  Sponsor,
  Stage,
  StripeConnectStatus,
  USER_GEAR_TYPES,
  USER_GENDERS,
  UserGender,
  USER_RACE_STATUSES,
  UserRace,
  UserRaceStatus,
  WORLD_TIME_ZONES,
  WorldTimeZone,
} from "../types/iosModels";
import { parseTaxRegistrationRule } from "./registrationPricing";

const LOCATION_KEYS = [
  "location",
  "coordinate",
  "startLocation",
  "endLocation",
  "point",
  "center",
] as const;

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

const asString = (value: unknown, fallback = ""): string => {
  return typeof value === "string" ? value : fallback;
};

const asStringOrNull = (value: unknown): string | null => {
  return typeof value === "string" ? value : null;
};

const asNumberOrNull = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const asIntOrNull = (value: unknown): number | null => {
  const parsed = asNumberOrNull(value);
  return parsed === null ? null : Math.trunc(parsed);
};

const asBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }

    if (normalized === "false") {
      return false;
    }
  }

  return fallback;
};

const asBooleanOrNull = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }

  if (value === undefined || value === null) {
    return null;
  }

  return asBoolean(value);
};

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => (typeof entry === "string" ? entry : null))
    .filter((entry): entry is string => Boolean(entry));
};

const parseCheckpointReachedIds = (record: Record<string, unknown>): string[] => {
  const checkpointReachedIds = asStringArray(record.checkpointReachedIds);
  if (checkpointReachedIds.length > 0) {
    return checkpointReachedIds;
  }

  return [
    asString(record.checkpointReachedId),
    asString(record.reachedCheckpointId),
    asString(record.latestCheckpointReachedId),
  ].filter(Boolean);
};

const asNumberArray = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => asNumberOrNull(entry))
    .filter((entry): entry is number => entry !== null);
};

const asStringMap = (value: unknown): Record<string, string> => {
  const record = asRecord(value);
  if (!record) {
    return {};
  }

  const result: Record<string, string> = {};
  Object.entries(record).forEach(([key, entry]) => {
    if (typeof entry === "string") {
      result[key] = entry;
    }
  });

  return result;
};

const asStringNumberMap = (value: unknown): Record<string, number> => {
  const record = asRecord(value);
  if (!record) {
    return {};
  }

  const result: Record<string, number> = {};
  Object.entries(record).forEach(([key, entry]) => {
    const numberValue = asNumberOrNull(entry);
    if (numberValue !== null) {
      result[key] = numberValue;
    }
  });

  return result;
};

const normalizeKey = (value: string): string => {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
};

const coerceEnum = <T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
  aliases?: Record<string, T>
): T => {
  if (typeof value !== "string") {
    return fallback;
  }

  const directMatch = allowed.find((entry) => entry === value);
  if (directMatch) {
    return directMatch;
  }

  const normalized = normalizeKey(value);

  if (aliases && aliases[normalized]) {
    return aliases[normalized];
  }

  const normalizedMatch = allowed.find((entry) => normalizeKey(entry) === normalized);
  return normalizedMatch || fallback;
};

export const toDateSafe = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }

  if (typeof record.toDate === "function") {
    try {
      const date = (record.toDate as () => Date)();
      return Number.isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }

  if ("seconds" in record) {
    const seconds = asNumberOrNull(record.seconds);
    const nanoseconds = asNumberOrNull(record.nanoseconds) ?? 0;

    if (seconds !== null) {
      const date = new Date(seconds * 1000 + nanoseconds / 1_000_000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  return null;
};

const parseSourceInformation = (value: unknown) => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  return {
    isSimulatedBySoftware: asBoolean(record.isSimulatedBySoftware),
    isProducedByAccessory: asBoolean(record.isProducedByAccessory),
  };
};

const parseDirectLocation = (value: unknown): KompeteLocation | null => {
  if (Array.isArray(value) && value.length >= 2) {
    const longitude = asNumberOrNull(value[0]);
    const latitude = asNumberOrNull(value[1]);

    if (latitude !== null && longitude !== null) {
      return { latitude, longitude };
    }
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const latitude =
    asNumberOrNull(record.latitude) ??
    asNumberOrNull(record.lat) ??
    asNumberOrNull(record._latitude);
  const longitude =
    asNumberOrNull(record.longitude) ??
    asNumberOrNull(record.lng) ??
    asNumberOrNull(record.lon) ??
    asNumberOrNull(record._longitude);

  if (latitude === null || longitude === null) {
    return null;
  }

  return {
    latitude,
    longitude,
    altitude: asNumberOrNull(record.altitude),
    horizontalAccuracy: asNumberOrNull(record.horizontalAccuracy),
    verticalAccuracy: asNumberOrNull(record.verticalAccuracy),
    speed: asNumberOrNull(record.speed),
    course: asNumberOrNull(record.course),
    timestamp: toDateSafe(record.timestamp),
    speedAccuracy: asNumberOrNull(record.speedAccuracy),
    courseAccuracy: asNumberOrNull(record.courseAccuracy),
    sourceInformation: parseSourceInformation(record.sourceInformation),
  };
};

export const parseLocation = (value: unknown): KompeteLocation | null => {
  const direct = parseDirectLocation(value);
  if (direct) {
    return direct;
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }

  for (const key of LOCATION_KEYS) {
    const nested = parseDirectLocation(record[key]);
    if (nested) {
      return nested;
    }
  }

  return null;
};

export const parseLocationArray = (value: unknown): KompeteLocation[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseLocation(entry))
    .filter((entry): entry is KompeteLocation => Boolean(entry));
};

const uniqueLocations = (locations: KompeteLocation[]): KompeteLocation[] => {
  const map = new Map<string, KompeteLocation>();

  locations.forEach((location) => {
    const key = `${location.latitude.toFixed(7)}:${location.longitude.toFixed(7)}`;
    if (!map.has(key)) {
      map.set(key, location);
    }
  });

  return Array.from(map.values());
};

const parseLineStringLocations = (value: unknown): KompeteLocation[] => {
  if (Array.isArray(value) && value.length > 1 && Array.isArray(value[0])) {
    return value
      .map((point) => parseDirectLocation(point))
      .filter((point): point is KompeteLocation => Boolean(point));
  }

  const record = asRecord(value);
  if (!record) {
    return [];
  }

  if (record.type === "LineString" && Array.isArray(record.coordinates)) {
    return parseLineStringLocations(record.coordinates);
  }

  if (record.geometry) {
    return parseLineStringLocations(record.geometry);
  }

  return [];
};

const parseRaceDifficulty = (value: unknown): RaceDifficulty => {
  return coerceEnum(value, RACE_DIFFICULTIES, "Moderate", {
    easy: "Easy",
    moderate: "Moderate",
    hard: "Hard",
    extreme: "Extreme",
  });
};

const parseRaceSurfaceType = (value: unknown): RaceSurfaceType => {
  return coerceEnum(value, RACE_SURFACE_TYPES, "Unknown", {
    singletrack: "Single Track",
  });
};

const parseRaceCategory = (value: unknown): RaceCategory => {
  return coerceEnum(value, RACE_CATEGORIES, "Undefined");
};

const parseRaceCategories = (value: unknown): RaceCategory[] => {
  return asStringArray(value).map((entry) => parseRaceCategory(entry));
};

const parseSportType = (value: unknown): SportType => {
  return coerceEnum(value, SPORT_TYPES, "Mixed", {
    roadbike: "Road Bike",
    mountainbike: "Mountain Bike",
    gravelbike: "Gravel Bike",
    eroadbike: "E-Road Bike",
    emountainbike: "E-Mountain Bike",
    egravelbike: "E-Gravel Bike",
    trailrun: "Trail Run",
    standuppaddle: "Stand Up Paddle",
    backcountryski: "Backcountry Ski",
    nordicski: "Nordic Ski",
    rollerblades: "Roller Blades",
    rollerskates: "Roller Skates",
  });
};

const parseRaceStartPolicy = (value: unknown): RaceStartPolicy => {
  return coerceEnum(value, RACE_START_POLICIES, "scheduled", {
    anytime: "userInitiated",
  });
};

const parseWorldTimeZone = (value: unknown): WorldTimeZone => {
  return coerceEnum(value, WORLD_TIME_ZONES, "UTC");
};

const parseEventStatus = (value: unknown): EventStatus => {
  return coerceEnum(value, EVENT_STATUSES, "unknown", {
    notstarted: "notStarted",
    inprogress: "inProgress",
    canceled: "cancelled",
  });
};

const parseEventType = (value: unknown): EventType => {
  return coerceEnum(value, EVENT_TYPES, "Race", {
    meetup: "Meet Up",
  });
};

const parseEventEntryType = (value: unknown): EventEntryType => {
  return coerceEnum(value, EVENT_ENTRY_TYPES, "Free", {
    free: "Free",
    paid: "Paid",
  });
};

const parseRefundPolicyType = (value: unknown): RefundPolicyType | null => {
  const normalized = asString(value).trim();
  return REFUND_POLICY_TYPES.includes(normalized as RefundPolicyType)
    ? (normalized as RefundPolicyType)
    : null;
};

const parseUserGender = (value: unknown): UserGender => {
  return coerceEnum(value, USER_GENDERS, "unknown", {
    male: "Male",
    female: "Female",
    nonbinary: "Non Binary",
    other: "Other",
  });
};

const parseUserGearType = (value: unknown) => {
  return coerceEnum(value, USER_GEAR_TYPES, "Other", {
    ebike: "E-Bike",
    emountainbike: "E-Mountain Bike",
    roadbike: "Road Bike",
    gravelbike: "Gravel Bike",
    downhillbike: "Downhill Bike",
    runningshoes: "Running Shoes",
    trailrunningshoes: "Trail Running Shoes",
    hikingboots: "Hiking Boots",
    climbingshoes: "Climbing Shoes",
    rollerskates: "Roller Skates",
    rollerblades: "Roller Blades",
    other: "Other",
  });
};

const parseEmergencyRelationship = (value: unknown) => {
  return coerceEnum(value, EMERGENCY_RELATIONSHIPS, "Other");
};

const parseUserRaceStatus = (value: unknown): UserRaceStatus => {
  return coerceEnum(value, USER_RACE_STATUSES, "Unknown", {
    notstarted: "Not Started",
    didnotshow: "Did Not Show",
    didnotfinishontime: "Did Not Finish On Time",
    didfinishincomplete: "Incomplete",
    inrace: "In Race",
    finishedlast: "Finished Last",
    canceled: "Cancelled",
    cancelled: "Cancelled",
  });
};

const parseRaceEnduranceFormat = (value: unknown): RaceEnduranceFormat | null => {
  if (!value) {
    return null;
  }

  return coerceEnum<RaceEnduranceFormat>(
    value,
    RACE_ENDURANCE_FORMATS,
    "threeHours",
    {
    threehours: "threeHours",
    sixhours: "sixHours",
    twelvehours: "twelveHours",
    twentyfourhours: "twentyFourHours",
    }
  );
};

const FORMAT_KEYS: Array<{ key: string; kind: RaceFormatKind }> = [
  { key: "startToFinish", kind: "startToFinish" },
  { key: "enduro", kind: "enduro" },
  { key: "downhill", kind: "downhill" },
  { key: "laps", kind: "laps" },
  { key: "checkpoints", kind: "checkpoints" },
  { key: "scavengerHunt", kind: "scavengerHunt" },
  { key: "globalOpenTimeTrial", kind: "globalOpenTimeTrial" },
  { key: "openCourseRacing", kind: "openCourseRacing" },
];

export const parseRaceFormat = (value: unknown): RaceFormat => {
  const record = asRecord(value);

  if (!record) {
    const kind = coerceEnum(value, RACE_FORMAT_KINDS, "startToFinish", {
      starttofinish: "startToFinish",
      scavengerhunt: "scavengerHunt",
      globalopentimetrial: "globalOpenTimeTrial",
      opencourseracing: "openCourseRacing",
    });

    return { kind };
  }

  const enduranceType = parseRaceEnduranceFormat(record.type);
  if (enduranceType) {
    return { kind: "endurance", enduranceType, raw: record };
  }

  for (const format of FORMAT_KEYS) {
    if (format.key in record) {
      return { kind: format.kind, raw: record };
    }
  }

  const kind = coerceEnum(record.kind, RACE_FORMAT_KINDS, "startToFinish", {
    starttofinish: "startToFinish",
    scavengerhunt: "scavengerHunt",
    globalopentimetrial: "globalOpenTimeTrial",
    opencourseracing: "openCourseRacing",
  });

  return { kind, raw: record };
};

export const parseSponsor = (value: unknown, fallbackId = ""): Sponsor | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  return {
    id: asString(record.id, fallbackId),
    name: asString(record.name, "Sponsor"),
    imageUrlString: asStringOrNull(record.imageUrlString),
    webUrlString: asStringOrNull(record.webUrlString),
  };
};

export const parseSponsors = (value: unknown): Sponsor[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => parseSponsor(entry, `sponsor-${index}`))
    .filter((entry): entry is Sponsor => Boolean(entry));
};

export const parseCheckpoint = (value: unknown, fallbackIndex: number): Checkpoint | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const location = parseLocation(record.location ?? record);
  if (!location) {
    return null;
  }

  return {
    id: asString(record.id, `checkpoint-${fallbackIndex}`),
    name: asString(record.name, `Checkpoint ${fallbackIndex + 1}`),
    number: asIntOrNull(record.number) ?? fallbackIndex + 1,
    location,
    radiusMeters: asNumberOrNull(record.radiusMeters),
    imageUrlString: asStringOrNull(record.imageUrlString),
  };
};

export const parseCheckpoints = (value: unknown): Checkpoint[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => parseCheckpoint(entry, index))
    .filter((entry): entry is Checkpoint => Boolean(entry));
};

export const parseAidStation = (value: unknown, fallbackIndex: number): AidStation | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const location = parseLocation(record.location ?? record);
  if (!location) {
    return null;
  }

  return {
    id: asString(record.id, `aid-station-${fallbackIndex}`),
    name: asString(record.name, `Aid Station ${fallbackIndex + 1}`),
    number: asIntOrNull(record.number) ?? fallbackIndex + 1,
    location,
    imagesUrls: asStringArray(record.imagesUrls),
  };
};

export const parseAidStations = (value: unknown): AidStation[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => parseAidStation(entry, index))
    .filter((entry): entry is AidStation => Boolean(entry));
};

export const parseRaceCue = (value: unknown, fallbackIndex: number): RaceCue | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const location = parseLocation(record.location ?? record);
  if (!location) {
    return null;
  }

  return {
    id: asString(record.id, `race-cue-${fallbackIndex}`),
    title: asString(record.title, `Race Cue ${fallbackIndex + 1}`),
    description: asString(record.description, ""),
    imageUrlString: asStringOrNull(record.imageUrlString),
    audioFileUrlString: asStringOrNull(record.audioFileUrlString),
    isActive: asBoolean(record.isActive, true),
    location,
  };
};

export const parseRaceCues = (value: unknown): RaceCue[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => parseRaceCue(entry, index))
    .filter((entry): entry is RaceCue => Boolean(entry));
};

export const parseStage = (value: unknown, fallbackIndex: number): Stage | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const stageLatitudes = asNumberArray(record.stageLatitudes);
  const stageLongitudes = asNumberArray(record.stageLongitudes);
  const reconstructed: KompeteLocation[] = [];

  if (stageLatitudes.length > 0 && stageLatitudes.length === stageLongitudes.length) {
    stageLatitudes.forEach((latitude, index) => {
      reconstructed.push({ latitude, longitude: stageLongitudes[index] });
    });
  }

  return {
    id: asString(record.id, `stage-${fallbackIndex}`),
    stageName: asString(record.stageName, `Stage ${fallbackIndex + 1}`),
    stageLocations: uniqueLocations([
      ...parseLocationArray(record.stageLocations),
      ...reconstructed,
    ]),
    stageStartLocation: parseLocation(
      record.stageStartLocation ?? record.stageStartocation
    ),
    stageEndLocation: parseLocation(record.stageEndLocation),
  };
};

export const parseStages = (value: unknown): Stage[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => parseStage(entry, index))
    .filter((entry): entry is Stage => Boolean(entry));
};

export const parseEvent = (value: unknown, fallbackId = ""): Event => {
  const record = asRecord(value) || {};

  const shortDescription = asString(
    record.shortDescription,
    asString(record.description, "")
  );

  return {
    id: asString(record.id, fallbackId),
    organizer: asString(record.organizer, ""),
    organizerPhotoFirebaseURLString: asStringOrNull(record.organizerPhotoFirebaseURLString),
    name: asString(record.name, "Untitled Event"),
    shortDescription,
    description: asString(record.description, shortDescription || "No description yet."),
    startDate: toDateSafe(record.startDate),
    endDate: toDateSafe(record.endDate),
    registrationOpenDate: toDateSafe(record.registrationOpenDate),
    registrationCloseDate: toDateSafe(record.registrationCloseDate),
    city: asString(record.city, ""),
    state: asStringOrNull(record.state),
    province: asStringOrNull(record.province),
    country: asString(record.country, ""),
    address: asString(record.address, ""),
    zipCode: asStringOrNull(record.zipCode),
    postalCode: asStringOrNull(record.postalCode),
    geoHash: asStringOrNull(record.geoHash),
    geoHashData: asStringMap(record.geoHashData),
    mobileCoverImageUrl: normalizeMediaUrl(record.mobileCoverImageUrl),
    webCoverImageUrl: normalizeMediaUrl(record.webCoverImageUrl),
    photoFirebaseURL: normalizeMediaUrl(record.photoFirebaseURL),
    photosFirebaseURLs: asStringArray(record.photosFirebaseURLs).map(normalizeMediaUrl).filter((url): url is string => url !== null),
    price: asString(record.price, ""),
    currency: asString(record.currency, ""),
    entryType: parseEventEntryType(record.entryType),
    refundPolicyType: parseRefundPolicyType(record.refundPolicyType),
    refundDeadline: toDateSafe(record.refundDeadline),
    isRefundable: asBoolean(record.isRefundable),
    maxAllowedParticipants: asIntOrNull(record.maxAllowedParticipants),
    waitListIds: asStringArray(record.waitListIds),
    isSoldOut: asBoolean(record.isSoldOut),
    status: parseEventStatus(record.status),
    isDiscoverable: asBoolean(record.isDiscoverable, true),
    wasCancelled: asBoolean(record.wasCancelled),
    isTest: asBoolean(record.isTest),
    eventTimeZone: parseWorldTimeZone(record.eventTimeZone),
    location: parseLocation(record.location),
    eventType: parseEventType(record.eventType),
    isFundRaiser: asBoolean(record.isFundRaiser),
    emailSubject: asString(record.emailSubject, ""),
    emailBody: asString(record.emailBody, ""),
    sponsors: parseSponsors(record.sponsors),
    venueAmenities: normalizeVenueAmenities(record.venueAmenities),
    defaultOpenWindowStart: toDateSafe(record.defaultOpenWindowStart),
    defaultOpenWindowEnd: toDateSafe(record.defaultOpenWindowEnd),
  };
};

const parseOptionalRaceSubmissionStatus = (
  value: unknown
): Race["submissionStatus"] => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === "pending" || normalized === "approved" || normalized === "denied") {
    return normalized;
  }

  return null;
};

const parseRacePaymentSettings = (value: unknown): RacePaymentSettings | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  return {
    provider: asStringOrNull(record.provider),
    feePolicyVersion: asStringOrNull(record.feePolicyVersion),
    platformFeeBps: asIntOrNull(record.platformFeeBps),
    organizerCommissionBps: asIntOrNull(record.organizerCommissionBps),
    organizerShareBps: asIntOrNull(record.organizerShareBps),
    racerServiceFeeBps: asIntOrNull(record.racerServiceFeeBps),
    pricingDisclosureVersion: asStringOrNull(record.pricingDisclosureVersion),
  };
};

const parseRegistrationPricingSnapshot = (
  value: unknown
): RegistrationPricingSnapshot | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const baseEntryAmount = asIntOrNull(record.baseEntryAmount);
  const racerServiceFeeAmount = asIntOrNull(record.racerServiceFeeAmount);
  const totalAmount = asIntOrNull(record.totalAmount);
  const platformCommissionAmount = asIntOrNull(
    record.platformCommissionAmount
  );
  const organizerTransferAmount = asIntOrNull(record.organizerTransferAmount);
  const platformRetainedAmount = asIntOrNull(record.platformRetainedAmount);
  const currencyExponent = asIntOrNull(record.currencyExponent);
  const organizerCommissionBps = asIntOrNull(record.organizerCommissionBps);
  const organizerShareBps = asIntOrNull(record.organizerShareBps);
  const racerServiceFeeBps = asIntOrNull(record.racerServiceFeeBps);

  if (
    baseEntryAmount === null ||
    racerServiceFeeAmount === null ||
    totalAmount === null ||
    platformCommissionAmount === null ||
    organizerTransferAmount === null ||
    platformRetainedAmount === null ||
    currencyExponent === null ||
    organizerCommissionBps === null ||
    organizerShareBps === null ||
    racerServiceFeeBps === null ||
    [
      baseEntryAmount,
      racerServiceFeeAmount,
      totalAmount,
      platformCommissionAmount,
      organizerTransferAmount,
      platformRetainedAmount,
      currencyExponent,
      organizerCommissionBps,
      organizerShareBps,
      racerServiceFeeBps,
    ].some((amount) => amount < 0)
  ) {
    return null;
  }

  return {
    baseEntryAmount,
    racerServiceFeeAmount,
    totalAmount,
    platformCommissionAmount,
    organizerTransferAmount,
    platformRetainedAmount,
    currency: asString(record.currency, "").trim().toUpperCase(),
    currencyExponent,
    organizerCommissionBps,
    platformFeeBps: asIntOrNull(record.platformFeeBps) ?? undefined,
    organizerShareBps,
    racerServiceFeeBps,
    feePolicyVersion: asStringOrNull(record.feePolicyVersion),
    pricingDisclosureVersion: asStringOrNull(record.pricingDisclosureVersion),
    taxPolicy: parseRegistrationTaxPolicy(record.taxPolicy),
  };
};

const parseRegistrationTaxPolicy = (
  value: unknown
): RegistrationTaxPolicy | null => {
  const record = asRecord(value);
  const raceEntry = asRecord(record?.raceEntry);
  const serviceFee = asRecord(record?.serviceFee);
  if (!record || !raceEntry || !serviceFee) {
    return null;
  }

  const parseComponent = (
    component: Record<string, unknown>
  ): RegistrationTaxPolicy["raceEntry"] => ({
    liableParty: asString(component.liableParty, "organizer"),
    taxability: asString(component.taxability, "organizer_determined"),
    priceIncludesTax: asBoolean(component.priceIncludesTax),
    taxCode: asStringOrNull(component.taxCode),
  });

  const hasRegistrationRule =
    record.registrationRule !== undefined && record.registrationRule !== null;
  const registrationRule = hasRegistrationRule
    ? parseTaxRegistrationRule(record.registrationRule)
    : null;
  if (hasRegistrationRule && !registrationRule) {
    return null;
  }

  return {
    version: asStringOrNull(record.version),
    integrationVersion: asStringOrNull(record.integrationVersion),
    disclosureVersion: asStringOrNull(record.disclosureVersion),
    jurisdiction: asStringOrNull(record.jurisdiction),
    determinationSource: asStringOrNull(record.determinationSource),
    configurationStatus: asStringOrNull(record.configurationStatus),
    reviewStatus: asStringOrNull(record.reviewStatus),
    registrationRule,
    raceEntry: parseComponent(raceEntry),
    serviceFee: parseComponent(serviceFee),
  };
};

const parseRegistrationTaxQuote = (
  value: unknown
): RegistrationTaxQuote | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const subtotalAmount = asIntOrNull(record.subtotalAmount);
  const taxAmountExclusive = asIntOrNull(record.taxAmountExclusive);
  const taxAmountInclusive = asIntOrNull(record.taxAmountInclusive);
  const taxAmount = asIntOrNull(record.taxAmount);
  const totalAmount = asIntOrNull(record.totalAmount);
  const integerAmounts = [
    subtotalAmount,
    taxAmountExclusive,
    taxAmountInclusive,
    taxAmount,
    totalAmount,
  ];
  const version = asString(record.version).trim();
  const disclosureVersion = asString(record.disclosureVersion).trim();
  const calculationId = asStringOrNull(record.calculationId)?.trim() || null;
  const inputFingerprint =
    asStringOrNull(record.inputFingerprint)?.trim() || null;
  const liableParty = asString(record.liableParty).trim().toLowerCase();
  const liableAccountId = asStringOrNull(record.liableAccountId)?.trim() || null;
  const currency = asString(record.currency).trim().toUpperCase();
  const performanceLocationId =
    asStringOrNull(record.performanceLocationId)?.trim() || null;
  const source = asStringOrNull(record.source)?.trim() || null;
  const isCheckoutManaged = source === "stripe_checkout_automatic_tax";

  if (
    integerAmounts.some(
      (amount) => amount === null || amount < 0 || !Number.isSafeInteger(amount)
    ) ||
    !version ||
    !disclosureVersion ||
    (!isCheckoutManaged && !calculationId) ||
    (!isCheckoutManaged && !inputFingerprint) ||
    !["organizer", "rauxa"].includes(liableParty) ||
    (!isCheckoutManaged && !liableAccountId) ||
    !/^[A-Z]{3}$/.test(currency) ||
    (!isCheckoutManaged && !performanceLocationId) ||
    subtotalAmount! + taxAmountExclusive! !== totalAmount ||
    taxAmountExclusive! + taxAmountInclusive! !== taxAmount
  ) {
    return null;
  }

  const rawBreakdown = Array.isArray(record.breakdown) ? record.breakdown : [];
  const breakdown = rawBreakdown.flatMap((value) => {
    const item = asRecord(value);
    const amount = asIntOrNull(item?.amount);
    const taxableAmount = asIntOrNull(item?.taxableAmount);
    if (
      !item ||
      amount === null ||
      taxableAmount === null ||
      amount < 0 ||
      taxableAmount < 0 ||
      !Number.isSafeInteger(amount) ||
      !Number.isSafeInteger(taxableAmount)
    ) {
      return [];
    }
    return [{
      amount,
      taxableAmount,
      inclusive: asBoolean(item.inclusive),
      country: asStringOrNull(item.country),
      state: asStringOrNull(item.state),
      taxType: asStringOrNull(item.taxType),
      percentageDecimal: asStringOrNull(item.percentageDecimal),
      taxabilityReason: asStringOrNull(item.taxabilityReason),
    }];
  });

  return {
    version,
    disclosureVersion,
    calculationId,
    inputFingerprint,
    liableParty,
    liableAccountId,
    currency,
    subtotalAmount: subtotalAmount!,
    taxAmountExclusive: taxAmountExclusive!,
    taxAmountInclusive: taxAmountInclusive!,
    taxAmount: taxAmount!,
    totalAmount: totalAmount!,
    performanceLocationId,
    breakdown,
    calculatedAt: toDateSafe(record.calculatedAt),
    expiresAt: toDateSafe(record.expiresAt),
    source,
    status: asStringOrNull(record.status),
  };
};

export const parseRaceProximitySettings = (value: unknown): RaceProximitySettings => {
  const record = asRecord(value) || {};
  const positiveRadiusOrDefault = (
    key: keyof RaceProximitySettings
  ): number => {
    const radius = asNumberOrNull(record[key]);
    return radius !== null && radius > 0
      ? radius
      : RACE_PROXIMITY_DEFAULTS[key];
  };

  return {
    checkInRadiusMeters: positiveRadiusOrDefault("checkInRadiusMeters"),
    checkpointRadiusMeters: positiveRadiusOrDefault("checkpointRadiusMeters"),
    raceCueRadiusMeters: positiveRadiusOrDefault("raceCueRadiusMeters"),
    aidStationRadiusMeters: positiveRadiusOrDefault("aidStationRadiusMeters"),
    finishRadiusMeters: positiveRadiusOrDefault("finishRadiusMeters"),
  };
};

const extractRoutePolyline = (input: {
  raceRecord: Record<string, unknown>;
  eventRecord: Record<string, unknown>;
  stages: Stage[];
  checkpoints: Checkpoint[];
}): KompeteLocation[] => {
  const { raceRecord, eventRecord, stages, checkpoints } = input;

  const directCandidates = [
    raceRecord.routePolyline,
    raceRecord.polylineLocations,
    raceRecord.routeLocations,
    raceRecord.routeCoordinates,
    eventRecord.routePolyline,
    eventRecord.polylineLocations,
    eventRecord.routeLocations,
    eventRecord.routeCoordinates,
  ];

  for (const candidate of directCandidates) {
    const parsed = uniqueLocations(parseLocationArray(candidate));
    if (parsed.length > 1) {
      return parsed;
    }
  }

  const lineCandidates = [
    raceRecord.routeGeoJSON,
    raceRecord.route,
    raceRecord.polyline,
    eventRecord.routeGeoJSON,
    eventRecord.route,
    eventRecord.polyline,
  ];

  for (const candidate of lineCandidates) {
    const parsed = uniqueLocations(parseLineStringLocations(candidate));
    if (parsed.length > 1) {
      return parsed;
    }
  }

  const stagePolyline = uniqueLocations(stages.flatMap((stage) => stage.stageLocations));
  if (stagePolyline.length > 1) {
    return stagePolyline;
  }

  const checkpointPolyline = uniqueLocations(
    checkpoints.map((checkpoint) => checkpoint.location)
  );

  if (checkpointPolyline.length > 1) {
    return checkpointPolyline;
  }

  return [];
};

export const parseRace = (value: unknown, fallbackId = ""): Race => {
  const raceRecord = asRecord(value) || {};
  const eventRecord = asRecord(raceRecord.event) || {};
  const insuranceRecord = asRecord(raceRecord.insurance);

  const raceId = asString(raceRecord.id, fallbackId);
  const eventId = asString(raceRecord.eventId, asString(eventRecord.id, raceId));

  const event = parseEvent(raceRecord.event, eventId);
  // Event owns venue metadata; an explicit empty event array clears the root fallback.
  const venueAmenities = normalizeVenueAmenities(
    eventRecord.venueAmenities ?? raceRecord.venueAmenities
  );
  event.venueAmenities = venueAmenities;

  const checkpoints = parseCheckpoints(raceRecord.checkpoints);

  const aidStations = parseAidStations(raceRecord.aidStations);
  const aidStationLocations = uniqueLocations([
    ...parseLocationArray(raceRecord.aidStationLocations),
    ...aidStations.map((station) => station.location),
  ]);

  const stages = parseStages(raceRecord.stages);
  const raceCues = parseRaceCues(raceRecord.raceCues);

  const startLocation = parseLocation(raceRecord.startLocation ?? event.location);
  const endLocation = parseLocation(raceRecord.endLocation);

  return {
    courseVerificationEnabled: raceRecord.courseVerificationEnabled === true,
    courseVerificationCourse: (asRecord(raceRecord.courseVerificationCourse) as VerificationCourse) || null,
    courseVerificationOffsets: Array.isArray(raceRecord.courseVerificationOffsets) ? raceRecord.courseVerificationOffsets as number[] : [],
    event,
    id: raceId,
    eventId,
    purchasesId: asString(
      raceRecord.purchasesId,
      raceId.length ? raceId.replace(/-/g, "_") : ""
    ),
    organizerUid: asStringOrNull(raceRecord.organizerUid),
    organizerEmail: asStringOrNull(raceRecord.organizerEmail),
    notificationEmail: asStringOrNull(raceRecord.notificationEmail),
    notifyOrganizerOnRegistration: raceRecord.notifyOrganizerOnRegistration === true,
    requestedDiscoverable:
      raceRecord.requestedDiscoverable === undefined
        ? null
        : asBoolean(raceRecord.requestedDiscoverable, event.isDiscoverable),
    submissionStatus: parseOptionalRaceSubmissionStatus(raceRecord.submissionStatus),
    requireParticipantId: raceRecord.requireParticipantId === true,
    insurance: insuranceRecord
      ? {
          coverageConfirmed: insuranceRecord.coverageConfirmed === true,
          platformDisclaimerAccepted: insuranceRecord.platformDisclaimerAccepted === true,
          version: asStringOrNull(insuranceRecord.version),
          acceptedBy: asStringOrNull(insuranceRecord.acceptedBy),
          acceptedAt: toDateSafe(insuranceRecord.acceptedAt),
        }
      : null,
    startDate: toDateSafe(raceRecord.startDate) || event.startDate,
    endDate: toDateSafe(raceRecord.endDate) || event.endDate,
    sponsors: parseSponsors(raceRecord.sponsors).length
      ? parseSponsors(raceRecord.sponsors)
      : event.sponsors,
    venueAmenities,
    startLocation,
    endLocation,
    checkpoints,
    aidStations,
    aidStationLocations,
    checkInLocation: parseLocation(raceRecord.checkInLocation) || startLocation,
    difficulty: parseRaceDifficulty(raceRecord.difficulty),
    rating: asNumberOrNull(raceRecord.rating),
    categories: parseRaceCategories(raceRecord.categories),
    leaderboard: asStringNumberMap(raceRecord.leaderboard),
    rules: asStringArray(raceRecord.rules),
    ruleDetails: normalizeRaceRules(raceRecord.ruleDetails ?? raceRecord.rules),
    registeredUserIds: asStringArray(raceRecord.registeredUserIds),
    checkedInUserIds: asStringArray(raceRecord.checkedInUserIds),
    didFinishIds: asStringArray(raceRecord.didFinishIds),
    didFinishIncompleteIds: asStringArray(raceRecord.didFinishIncompleteIds),
    didNotFinishOnTimeIds: asStringArray(raceRecord.didNotFinishOnTimeIds),
    didNotShowIds: asStringArray(raceRecord.didNotShowIds),
    disqualifiedIds: asStringArray(raceRecord.disqualifiedIds),
    didFinishLastId: asStringOrNull(raceRecord.didFinishLastId),
    abandonedIds: asStringArray(raceRecord.abandonedIds),
    podiumIds: asStringArray(raceRecord.podiumIds),
    winnerId: asStringOrNull(raceRecord.winnerId),
    prizes: asStringArray(raceRecord.prizes ?? raceRecord.prices),
    totalLaps: asIntOrNull(raceRecord.totalLaps),
    checkInStartTime: toDateSafe(raceRecord.checkInStartTime),
    checkInCloseTime: toDateSafe(raceRecord.checkInCloseTime),
    format: parseRaceFormat(raceRecord.format),
    sportType: parseSportType(raceRecord.sportType),
    gpxFileFirebaseURLString: asStringOrNull(raceRecord.gpxFileFirebaseURLString),
    podiumCeremonyDate: toDateSafe(raceRecord.podiumCeremonyDate),
    waiver: asString(raceRecord.waiver, ""),
    raceMeetingTime: toDateSafe(raceRecord.raceMeetingTime),
    stages,
    length: asNumberOrNull(raceRecord.length),
    surfaceType: parseRaceSurfaceType(raceRecord.surfaceType),
    totalAscent: asNumberOrNull(raceRecord.totalAscent),
    totalDescent: asNumberOrNull(raceRecord.totalDescent),
    isCheckInEnabled: asBooleanOrNull(raceRecord.isCheckInEnabled),
    targetDistance: asNumberOrNull(raceRecord.targetDistance),
    isFlexibleRoute: asBoolean(raceRecord.isFlexibleRoute),
    allowsReEntry: asBoolean(raceRecord.allowsReEntry),
    offlineResultSyncGraceHours:
      asNumberOrNull(raceRecord.offlineResultSyncGraceHours) ??
      (asNumberOrNull(raceRecord.offlineResultSyncGraceMinutes) !== null
        ? (asNumberOrNull(raceRecord.offlineResultSyncGraceMinutes) as number) / 60
        : null) ??
      (parseRaceStartPolicy(raceRecord.startPolicy) === "userInitiated" ? 1 : 0.25),
    raceCues,
    enforceOffRoute: asBoolean(raceRecord.enforceOffRoute),
    showsRacersLocations: asBoolean(raceRecord.showsRacersLocations, true),
    shouldBypassLocationCheckIn: asBoolean(raceRecord.shouldBypassLocationCheckIn),
    isCancelled: asBoolean(raceRecord.isCancelled, event.wasCancelled),
    startPolicy: parseRaceStartPolicy(raceRecord.startPolicy),
    effectiveOpenWindowStart: toDateSafe(raceRecord.openWindowStart) || toDateSafe(raceRecord.effectiveOpenWindowStart),
    effectiveOpenWindowEnd: toDateSafe(raceRecord.openWindowEnd) || toDateSafe(raceRecord.effectiveOpenWindowEnd),
    routePolyline: extractRoutePolyline({
      raceRecord,
      eventRecord,
      stages,
      checkpoints,
    }),
    paymentSettings: parseRacePaymentSettings(raceRecord.paymentSettings),
    pricing: parseRegistrationPricingSnapshot(
      raceRecord.pricing ?? (asRecord(raceRecord.paymentSettings) || {}).pricing
    ),
    publishBlockReason: asStringOrNull(raceRecord.publishBlockReason),
    minimumFinishTimeSeconds: Math.min(86400, Math.max(0, asIntOrNull(raceRecord.minimumFinishTimeSeconds) ?? 0)),
    proximitySettings: parseRaceProximitySettings(raceRecord.proximitySettings),
    resultsFinalizedAt: toDateSafe(raceRecord.resultsFinalizedAt),
    resultsNotificationsCompletedAt: toDateSafe(raceRecord.resultsNotificationsCompletedAt),
    testResetGeneration: Math.max(
      0,
      asIntOrNull(raceRecord.testResetGeneration) ?? 0
    ),
  };
};

export const parseGearItem = (value: unknown, fallbackId = ""): GearItem | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  return {
    id: asString(record.id, fallbackId),
    type: parseUserGearType(record.type),
    model: asString(record.model, ""),
    totalTimeUsed: asNumberOrNull(record.totalTimeUsed) ?? 0,
    totalDistanceUsed: asNumberOrNull(record.totalDistanceUsed) ?? 0,
  };
};

export const parseGearItems = (value: unknown): GearItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => parseGearItem(entry, `gear-${index}`))
    .filter((entry): entry is GearItem => Boolean(entry));
};

export const parseEmergencyContact = (value: unknown): EmergencyContact | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  return {
    name: asString(record.name, ""),
    phoneNumber: asString(record.phoneNumber, ""),
    relationship: parseEmergencyRelationship(record.relationship),
  };
};

const parseStripeConnectStatus = (value: unknown): StripeConnectStatus | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  return {
    accountId: asStringOrNull(record.accountId),
    onboardingComplete: asBoolean(record.onboardingComplete),
    chargesEnabled: asBoolean(record.chargesEnabled),
    payoutsEnabled: asBoolean(record.payoutsEnabled),
    detailsSubmitted: asBoolean(record.detailsSubmitted),
    disabledReason: asStringOrNull(record.disabledReason),
    lastAccountUpdateAt: toDateSafe(record.lastAccountUpdateAt),
  };
};

export const parseUser = (value: unknown, fallbackId = ""): KompeteUser => {
  const record = asRecord(value) || {};

  return {
    id: asString(record.id, fallbackId),
    userName: asString(record.userName, ""),
    email: asString(record.email, ""),
    appleIdToken: asStringOrNull(record.appleIdToken),
    legalFirstName: asStringOrNull(record.legalFirstName),
    legalLastName: asStringOrNull(record.legalLastName),
    birthDate: toDateSafe(record.birthDate),
    gender: record.gender ? parseUserGender(record.gender) : null,
    bio: asStringOrNull(record.bio),
    fcmToken: asStringOrNull(record.fcmToken),
    city: asStringOrNull(record.city),
    state: asStringOrNull(record.state),
    country: asStringOrNull(record.country),
    legalAddress: asStringOrNull(record.legalAddress),
    profilePhotoURLString: asStringOrNull(record.profilePhotoURLString),
    isRacing: asBoolean(record.isRacing),
    didAgreeToTermsAndConditions: asBoolean(record.didAgreeToTermsAndConditions),
    registeredRacesIds: asStringArray(record.registeredRacesIds),
    bookmarkedRacesIds: asStringArray(record.bookmarkedRacesIds),
    friendUserIds: asStringArray(record.friendUserIds),
    blockedUserIds: asStringArray(record.blockedUserIds),
    followingUserIds: asStringArray(record.followingUserIds),
    isBannedFromRacing: asBoolean(record.isBannedFromRacing),
    gear: parseGearItems(record.gear),
    emergencyContact: parseEmergencyContact(record.emergencyContact),
    totalDistanceRaced: asNumberOrNull(record.totalDistanceRaced) ?? 0,
    totalRacesFinished: asIntOrNull(record.totalRacesFinished) ?? 0,
    totalRacesWon: asIntOrNull(record.totalRacesWon) ?? 0,
    createdRaceIds: asStringArray(record.createdRaceIds),
    isAppleTester: asBoolean(record.isAppleTester),
    registrationDate: toDateSafe(record.registrationDate),
    signupPlatform: isSignupPlatform(record.signupPlatform)
      ? record.signupPlatform
      : null,
    signupAuthMethod: isSignupAuthMethod(record.signupAuthMethod)
      ? record.signupAuthMethod
      : null,
    acceptedOrganizerPayoutTerms: asBoolean(record.acceptedOrganizerPayoutTerms),
    organizerPayoutTermsAcceptedAt: toDateSafe(record.organizerPayoutTermsAcceptedAt),
    organizerPayoutTermsVersion: asStringOrNull(record.organizerPayoutTermsVersion),
    stripeConnect: parseStripeConnectStatus(record.stripeConnect),
  };
};

const parseLapsTime = (value: unknown): Record<string, Date> => {
  const record = asRecord(value);
  if (!record) {
    return {};
  }

  const result: Record<string, Date> = {};
  Object.entries(record).forEach(([key, entry]) => {
    const date = toDateSafe(entry);
    if (date) {
      result[key] = date;
    }
  });

  return result;
};

export const parseUserRace = (value: unknown, fallbackId = ""): UserRace => {
  const record = asRecord(value) || {};

  return {
    courseVerificationRequired: record.courseVerificationRequired === true,
    courseVerification: (asRecord(record.courseVerification) as CourseVerification) || null,
    courseVerificationCourse: (asRecord(record.courseVerificationCourse) as VerificationCourse) || null,
    id: asString(record.id, fallbackId),
    name: asString(record.name, ""),
    userId: asString(record.userId, ""),
    raceId: asString(record.raceId, ""),
    category: parseRaceCategory(record.category),
    format: parseRaceFormat(record.format),
    sportType: parseSportType(record.sportType),
    plateNumber: parsePlateNumber(record.plateNumber),
    lapNumber: asIntOrNull(record.lapNumber),
    totalLaps: asIntOrNull(record.totalLaps),
    lapsTime: parseLapsTime(record.lapsTime),
    raceTime: asNumberOrNull(record.raceTime),
    finalPosition: asIntOrNull(record.finalPosition),
    raceStartLocation: parseLocation(record.raceStartLocation),
    raceEndLocation: parseLocation(record.raceEndLocation),
    attemptEndLocation: parseLocation(record.attemptEndLocation),
    currentUserLocation: parseLocation(record.currentUserLocation),
    locations: parseLocationArray(record.locations),
    aidStationLocations: parseLocationArray(record.aidStationLocations),
    checkpointLocations: parseLocationArray(record.checkpointLocations),
    checkpointReachedIds: asStringArray(record.checkpointReachedIds),
    stages: parseStages(record.stages),
    status: parseUserRaceStatus(record.status),
    startDate: toDateSafe(record.startDate),
    checkInStartTime: toDateSafe(record.checkInStartTime),
    checkInCloseTime: toDateSafe(record.checkInCloseTime),
    didCheckIn: asBoolean(record.didCheckIn),
    checkInUserTime: toDateSafe(record.checkInUserTime),
    isTest: asBoolean(record.isTest),
    stageStartTime: toDateSafe(record.stageStartTime),
    gpxFileFirebaseURLString: asStringOrNull(record.gpxFileFirebaseURLString),
    endDate: toDateSafe(record.endDate),
    garminInReachSharePageURLString: asStringOrNull(
      record.garminInReachSharePageURLString
    ),
    spotSharePageURLString: asStringOrNull(record.spotSharePageURLString),
    finishedTime: asNumberOrNull(record.finishedTime),
    averageSpeed: asNumberOrNull(record.averageSpeed),
    elevationGained: asNumberOrNull(record.elevationGained),
    totalDistance: asNumberOrNull(record.totalDistance),
    caloriesBurned: asIntOrNull(record.caloriesBurned),
    gearUsed: parseGearItem(record.gearUsed),
    userStartTime: toDateSafe(record.userStartTime),
    userEndTime: toDateSafe(record.userEndTime),
    attemptNumber: asIntOrNull(record.attemptNumber),
    countdownMovementWarningCount: asIntOrNull(record.countdownMovementWarningCount) ?? 0,
    createdAt: toDateSafe(record.createdAt),
    updatedAt: toDateSafe(record.updatedAt),
    startPolicy: record.startPolicy ? parseRaceStartPolicy(record.startPolicy) : null,
    testResetGeneration: Math.max(
      0,
      asIntOrNull(record.testResetGeneration) ?? 0
    ),
  };
};

const parseRegistrationStatus = (value: unknown): RegistrationRecord["status"] => {
  const normalized = asString(value);
  return REGISTRATION_STATUSES.includes(
    normalized as RegistrationRecord["status"]
  )
    ? (normalized as RegistrationRecord["status"])
    : "initiated";
};

const parseRefundStatus = (value: unknown): RegistrationRecord["refundStatus"] => {
  const normalized = asString(value);
  return REFUND_STATUSES.includes(normalized as RegistrationRecord["refundStatus"])
    ? (normalized as RegistrationRecord["refundStatus"])
    : "none";
};

export const parseRegistration = (
  value: unknown,
  fallbackId = ""
): RegistrationRecord => {
  const record = asRecord(value) || {};

  return {
    id: asString(record.id, fallbackId),
    raceId: asString(record.raceId, ""),
    raceName: asString(record.raceName, ""),
    organizerId: asString(record.organizerId, ""),
    participantUserId: asString(record.participantUserId, ""),
    userRaceId: asString(record.userRaceId, ""),
    plateNumber: parsePlateNumber(record.plateNumber),
    stripeAccountId: asStringOrNull(record.stripeAccountId),
    status: parseRegistrationStatus(record.status),
    amount: asNumberOrNull(record.amount) ?? 0,
    currency: asString(record.currency, ""),
    platformFeeBps: asIntOrNull(record.platformFeeBps) ?? 0,
    applicationFeeAmount: asIntOrNull(record.applicationFeeAmount) ?? 0,
    feePolicyVersion: asStringOrNull(record.feePolicyVersion),
    pricing: parseRegistrationPricingSnapshot(record.pricing),
    paymentFlowVersion: asStringOrNull(record.paymentFlowVersion),
    taxQuote: parseRegistrationTaxQuote(record.taxQuote),
    taxTransactionId: asStringOrNull(record.taxTransactionId),
    taxTransactionStatus: asStringOrNull(record.taxTransactionStatus),
    paymentIntentId: asStringOrNull(record.paymentIntentId),
    checkoutSessionId: asStringOrNull(record.checkoutSessionId),
    chargeId: asStringOrNull(record.chargeId),
    refundStatus: parseRefundStatus(record.refundStatus),
    refundedAmount: asNumberOrNull(record.refundedAmount) ?? 0,
    participantProfileSnapshot: asRecord(record.participantProfileSnapshot) || {},
    raceCategory: asString(record.raceCategory, "Open"),
    gearUsed: parseGearItem(record.gearUsed),
    paymentConfirmedAt: toDateSafe(record.paymentConfirmedAt),
    refundedAt: toDateSafe(record.refundedAt),
    createdAt: toDateSafe(record.createdAt),
    updatedAt: toDateSafe(record.updatedAt),
  };
};

export const parseRacerLocation = (
  value: unknown,
  fallbackId = ""
): RacerLocation | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const location = parseLocation(record.location ?? record);
  if (!location || !Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)
    || Math.abs(location.latitude) > 90 || Math.abs(location.longitude) > 180) {
    return null;
  }

  const documentId = asString(record.id, fallbackId);
  const payloadUserId = asString(record.userId, documentId || fallbackId);

  // Discard tampered entries where payload userId does not match document id.
  if (documentId && payloadUserId && payloadUserId !== documentId) {
    return null;
  }

  const canonicalUserId = documentId || payloadUserId;
  if (!canonicalUserId) {
    return null;
  }

  return {
    id: documentId || canonicalUserId,
    userId: canonicalUserId,
    userName: asString(record.userName, "Anonymous Racer"),
    userProfileImageUrlString: asStringOrNull(record.userProfileImageUrlString),
    lastUpdated: toDateSafe(record.lastUpdated),
    location,
    status: parseUserRaceStatus(record.status),
    checkpointReachedIds: parseCheckpointReachedIds(record),
    testResetGeneration: Math.max(
      0,
      asIntOrNull(record.testResetGeneration) ?? 0
    ),
  };
};

export const parseFeatureFlags = (value: unknown): FeatureFlags => {
  const record = asRecord(value) || {};

  return {
    shouldEnableAppleSignIn: asBoolean(record.shouldEnableAppleSignIn),
    shouldShowColorSchemeOption: asBoolean(record.shouldShowColorSchemeOption),
    shouldUseLocationToFetchRaces: asBoolean(record.shouldUseLocationToFetchRaces),
  };
};

export const parseLegalDisclaimer = (value: unknown): LegalDisclaimer => {
  const record = asRecord(value) || {};

  return {
    user: parseUser(record.user),
    date: toDateSafe(record.date),
    didAgreeToTerms: asBoolean(
      record.didAgreeToTerms ?? record.didAgreeToTermsAndConditions
    ),
  };
};

export const parseRaceResult = (value: unknown, fallbackId = ""): RaceResult => {
  const record = asRecord(value) || {};

  return {
    id: asString(record.id, fallbackId),
    userName: asString(record.userName, ""),
    userId: asString(record.userId, ""),
    profileImageUrlString: asStringOrNull(record.profileImageUrlString),
    position: asIntOrNull(record.position),
    userRace: parseUserRace(record.userRace),
    finalResultsComputedAt: toDateSafe(record.finalResultsComputedAt),
  };
};

export const getRaceName = (race: Race): string => {
  return race.event.name || "Untitled race";
};

export const getRaceDescription = (race: Race): string => {
  return race.event.shortDescription || race.event.description || "No description yet.";
};

export const getRaceCity = (race: Race): string => {
  return race.event.city || "";
};

export const getRaceCountry = (race: Race): string => {
  return race.event.country || "";
};

export const getRaceAddress = (race: Race): string => {
  return race.event.address || "";
};

export const getRaceCheckpointLocations = (race: Race): KompeteLocation[] => {
  return uniqueLocations(race.checkpoints.map((checkpoint) => checkpoint.location));
};

export const getRaceRoutePolyline = (race: Race): KompeteLocation[] => {
  if (race.routePolyline.length > 1) {
    return race.routePolyline;
  }

  const stagePolyline = uniqueLocations(race.stages.flatMap((stage) => stage.stageLocations));
  if (stagePolyline.length > 1) {
    return stagePolyline;
  }

  const checkpointPolyline = getRaceCheckpointLocations(race);
  if (checkpointPolyline.length > 1) {
    return checkpointPolyline;
  }

  return [];
};

export const getRaceEffectiveStartDate = (race: Race): Date | null => {
  if (race.startPolicy === "userInitiated") {
    return (
      race.effectiveOpenWindowStart ||
      race.event.defaultOpenWindowStart ||
      race.startDate ||
      race.event.startDate ||
      null
    );
  }

  return race.startDate || race.event.startDate || null;
};

export const getRaceEffectiveEndDate = (race: Race): Date | null => {
  if (race.startPolicy === "userInitiated") {
    return (
      race.effectiveOpenWindowEnd ||
      race.event.defaultOpenWindowEnd ||
      race.endDate ||
      race.event.endDate ||
      null
    );
  }

  return race.endDate || race.event.endDate || null;
};

export type RaceRegistrationAvailabilityReason =
  | "open"
  | "notOpenYet"
  | "closed"
  | "soldOut"
  | "cancelled";

export type RaceRegistrationAvailability = {
  isOpen: boolean;
  reason: RaceRegistrationAvailabilityReason;
  opensAt: Date | null;
  closesAt: Date | null;
};

export const getRaceRegistrationAvailability = (
  race: Race,
  now: Date = new Date()
): RaceRegistrationAvailability => {
  const opensAt = race.event.registrationOpenDate;
  const closesAt = race.event.registrationCloseDate;

  if (race.isCancelled || race.event.wasCancelled || race.event.status === "cancelled") {
    return { isOpen: false, reason: "cancelled", opensAt, closesAt };
  }

  const reachedCapacity =
    race.event.maxAllowedParticipants !== null &&
    race.event.maxAllowedParticipants > 0 &&
    race.registeredUserIds.length >= race.event.maxAllowedParticipants;

  if (race.event.isSoldOut || reachedCapacity) {
    return { isOpen: false, reason: "soldOut", opensAt, closesAt };
  }

  const nowMs = now.getTime();
  if (opensAt && nowMs < opensAt.getTime()) {
    return { isOpen: false, reason: "notOpenYet", opensAt, closesAt };
  }

  if (closesAt && nowMs > closesAt.getTime()) {
    return { isOpen: false, reason: "closed", opensAt, closesAt };
  }

  return { isOpen: true, reason: "open", opensAt, closesAt };
};
